import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ExternalLink, ClipboardCheck, Clock, AlertCircle } from "lucide-react";

const WORK_TYPE_LABEL = {
  ASSIGNMENT: "Aufgabe",
  SHORT_ANSWER_QUESTION: "Kurze Antwort",
  MULTIPLE_CHOICE_QUESTION: "Multiple-Choice",
  MATERIAL: "Material",
};

/**
 * Widget: upcoming Google Classroom coursework for a class.
 * Renders inline (used both in ClassDetailPage and ParticipantHomePage).
 */
export default function GoogleCourseworkWidget({ classId, lang = "de" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/google/classes/${classId}/coursework`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [classId]);

  const T = lang === "en" ? {
    heading: "Upcoming Classroom assignments",
    loading: "Loading Classroom assignments…",
    empty: "No upcoming assignments in Google Classroom.",
    not_connected: "Google Classroom is not yet linked for this class.",
    not_connected_admin: "Ask an admin to connect Google Classroom in the Weiterbildung section.",
    no_google: "Google Classroom is not connected yet.",
    api_error: "Google Classroom is temporarily unreachable.",
    due: "Due",
    open_in_classroom: "Open in Classroom",
    no_due_extra: "Without deadline",
  } : {
    heading: "Nächste Aufgaben aus Google Classroom",
    loading: "Aufgaben werden geladen…",
    empty: "Keine kommenden Aufgaben in Google Classroom.",
    not_connected: "Für diese Klasse ist noch kein Google Classroom hinterlegt.",
    not_connected_admin: "Bitte im Weiterbildungs-Bereich eine Classroom-URL eintragen.",
    no_google: "Google Classroom ist noch nicht verbunden.",
    api_error: "Google Classroom ist gerade nicht erreichbar.",
    due: "Fällig",
    open_in_classroom: "In Classroom öffnen",
    no_due_extra: "Ohne Frist",
  };

  if (loading) {
    return <div className="text-xs text-slate-500 p-4 border border-slate-200" data-testid="coursework-loading">{T.loading}</div>;
  }

  if (error) {
    // 409 with specific messages — show friendly hints
    const msg = String(error);
    const isNoConn = msg.toLowerCase().includes("nicht verbunden") || msg.toLowerCase().includes("nicht hinterlegt") || msg.toLowerCase().includes("abgelaufen");
    return (
      <div className="border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900 flex items-start gap-2" data-testid="coursework-error">
        <AlertCircle size={14} strokeWidth={1.5} className="shrink-0 mt-0.5" />
        <div>{isNoConn ? msg : T.api_error}</div>
      </div>
    );
  }

  const items = data?.upcoming || [];
  const noDue = data?.no_due || [];

  return (
    <div className="border border-slate-200 bg-white" data-testid="coursework-widget">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <img src="https://ssl.gstatic.com/classroom/logo_square_rounded.svg" alt="" className="w-5 h-5" />
        <div className="text-xs font-bold uppercase tracking-overline text-slate-700">{T.heading}</div>
      </div>
      {items.length === 0 && noDue.length === 0 ? (
        <div className="p-6 text-center text-xs text-slate-500 italic">{T.empty}</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((it) => {
            const due = it.due_at ? new Date(it.due_at) : null;
            return (
              <div key={it.id} className="p-4 flex items-start gap-3" data-testid={`coursework-item-${it.id}`}>
                <ClipboardCheck size={16} strokeWidth={1.5} className="text-[#1a73e8] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{it.title}</div>
                  {it.description && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{it.description}</div>}
                  <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-1 flex-wrap">
                    {due && (
                      <span className="inline-flex items-center gap-1 font-mono">
                        <Clock size={10} strokeWidth={1.5} />
                        {T.due}: {new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "de-DE", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Berlin" }).format(due)}
                      </span>
                    )}
                    {it.work_type && <span className="uppercase tracking-overline">{WORK_TYPE_LABEL[it.work_type] || it.work_type}</span>}
                    {it.max_points != null && <span>{it.max_points} Punkte</span>}
                  </div>
                </div>
                {it.alternate_link && (
                  <a
                    href={it.alternate_link}
                    target="_blank" rel="noopener noreferrer"
                    className="shrink-0 text-[10px] uppercase text-[#1a73e8] hover:underline flex items-center gap-1"
                    data-testid={`coursework-open-${it.id}`}
                  >
                    <ExternalLink size={10} /> {T.open_in_classroom}
                  </a>
                )}
              </div>
            );
          })}
          {noDue.length > 0 && (
            <details className="p-3 text-xs">
              <summary className="cursor-pointer text-slate-500 font-medium">{T.no_due_extra} ({noDue.length})</summary>
              <div className="mt-2 space-y-1 pl-4">
                {noDue.map((n) => (
                  <div key={n.id} className="text-slate-600">
                    <a href={n.alternate_link} target="_blank" rel="noopener noreferrer" className="hover:underline">{n.title}</a>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
